/* eslint-disable no-unused-expressions */
/* eslint-disable no-param-reassign */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

// this input is ported from out npm package `cf-components-ae-order-processing` "AOPBaseSelect" but
// modified to remove aop functionality and downgraded for React 16.3
// feel free to "extend" but do not "modify" this input (open-closed principle)
class BaseSelect extends PureComponent {
  constructor(props) {
    super(props);

    const { value } = props;

    this.state = {
      value,
      cursor: -1,
      uiValue: null,
      isValid: Boolean(value), // since any option selected is valid
      isTouched: false,
      isFocused: false,
      userQuery: '',
    }

    // bound for event removal
    this.handleClick = this.handleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  /**
   * Do this here to prevent render blocking entire component while this computes
   */
  componentDidMount() {
    const { optionData } = this.props;
    // since this isn't a normal "select" element, where it displays selected "option" text based
    // on "select" value, we must do that manually using this map
    this.valueTextMap = optionData.reduce((result, option) => {
      const { text, value } = option;
      result[value] = text;

      return result;
    }, {});

    this.setState(({ value }) => ({
      uiValue: this.valueTextMap[value],
    }));
  }

  /**
   * since there can be many of these Select components on a page at once
   * but only one mousedown event can exist on the document at a time
   * we add and remove on focus/blur instead of on mount/unmout
   */
  componentDidUpdate = (_, { isFocused: wasFocused }) => {
    const { isFocused } = this.state;

    if (!wasFocused && isFocused) {
      document.addEventListener('mousedown', this.handleClick);
    } else if (wasFocused && !isFocused) {
      document.removeEventListener('mousedown', this.handleClick);
    }
  }

  /**
   * in the case of "refreshing" sometimes event remain on document
   */
  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClick);
  }

  /**
   * blurs hidden input and updates state accordingly
   */
  handleBlur = () => {
    this.hiddenInput.blur();
    this.setState({ isTouched: true, isFocused: false });
  }

  /**
   * focuses hidden input and updates state accordingly
   */
  handleFocus = () => {
    this.hiddenInput.focus();
    this.setState({ isFocused: true });
  }

  /**
   * calls parent's event handler and updates state setting new uiValue
   * @param {*} value - the value of option selectd; can be any type
   * @param {object} e - the event object
   */
  handleChange = (value, e) => {
    // stop propagation so the click event isn't also registered (so dropdown closes on selection)
    e && e.stopPropagation();

    const { inputKey, eventHandler, coachingFormType } = this.props;
    //process flow evals allow for optional fields
    const isValid = coachingFormType === 3 || Boolean(value);
    const uiValue = this.valueTextMap[value];

    eventHandler(value, inputKey, isValid);
    this.setState({
      isFocused: false,
      isValid,
      uiValue,
      value,
    });
  }

  /**
   * Handles Up, Down, & Enter keys, preventing default behavior when needed
   * this prevent the arrow down key scrolling the page and controlling input
   * @param {object} e - The Event object
   */
  handleKeyDown = (e) => {
    const { keyCode } = e;
    const { cursor } = this.state;
    const { optionData } = this.props;

    // do not allow arrow key if already hovering to prevent to active options
    if (!this.hoveringOption) {
      // "up"
      if (keyCode === 38 && cursor >= 0) {
        e.preventDefault();
        this.setState((prevState) => ({
          cursor: prevState.cursor - 1,
        }));
        // "down"
      } else if (keyCode === 40 && cursor < optionData.length - 1) {
        e.preventDefault();
        this.setState((prevState) => ({
          cursor: prevState.cursor + 1,
        }));
        // "enter"
      } else if (keyCode === 13 && cursor >= 0 && optionData.length > 0) {
        e.preventDefault();
        const { value = '' } = optionData[cursor];

        this.handleBlur();
        this.handleChange(value, e);
      }
    }
  }

  /**
   * This is to prevent two options from being active from arrow keys + hover effect
   */
  handleOptionHover = () => {
    if (!this.hoveringOption) this.hoveringOption = true;

    if (this.state.cursor >= 0) {
      this.setState({ cursor: -1 });
    }
  }

  /**
   * This is to prevent two options from being active from arrow keys + hover effect
   */
  handleOptionLeave = () => {
    if (this.hoveringOption) this.hoveringOption = false;
  }

  /**
   * get options add any active styles based on cursor position
   */
  getOptions = () => {
    const {
      optionData,
      optionStyles,
      optionClassNames,
    } = this.props;

    const { cursor } = this.state;

    let isOptionActive = false;
    const options = optionData.map(({ key, text, value }, i) => {
      const isActiveOption = cursor === i;
      const optionState = isActiveOption ? '--active' : '';

      return (
        <li
          onClick={(e) => this.handleChange(value, e)}
          className={`base-select__option${optionState} ${optionClassNames}`}
          ref={(option) => {
            if (isActiveOption) {
              isOptionActive = true;
              this.activeOption = option;
            }
          }}
          onMouseEnter={this.handleOptionHover}
          onMouseLeave={this.handleOptionLeave}
          style={optionStyles}
          key={key}
        >
          {text}
        </li>
      );
    });

    // clear stale ref if no option is active
    if (!isOptionActive) {
      this.activeOption = null;
    }

    return options;
  }

  /**
   * Clears all user input, refocuses input, updates state, updates parent
   */
  handleXDelete = () => {
    this.handleFocus();
    this.handleChange('');
  }

  /**
   * Handles click on dropdown arrow, updates state, updates parent
   */
  handleArrow = (e) => {
    // prevent event bubbling
    e.stopPropagation();

    const { isFocused } = this.state;
    this.setState({ isFocused: !isFocused })
  }

  /**
   * a real "select" after about 2 seconds, starts your query over again
   * this clears the user's invisible query, so they can search a new option
   */
  clearUserQuery = () => {
    this.setState({ userQuery: '' });
    this.timerSet = false;
  }

  /**
   * this mimics a native browsers "select" element
   * */
  handleUserQuery = ({ target: { value } }) => {
    const { optionData } = this.props;
    const regex = new RegExp(value, 'gi');
    const cursor = optionData.findIndex(({ text }) => regex.test(text));

    this.setState({ userQuery: value, cursor }, () => {
      if (this.activeOption) {
        const { offsetTop } = this.activeOption;
        // minus 56 is for padding
        this.dropDown.scrollTop = offsetTop - 56;
      }

      clearTimeout(this.timer);
      this.timer = setTimeout(this.clearUserQuery, 2000);
    });
  }

  /**
   * handles a click outside of component
   * @param {object} e - the click event
   */
  handleClick({ target }) {
    if (!this.node.contains(target)) {
      this.handleBlur();
    }
  }

  /**
   * renders UI
   */
  render() {
    const {
      isDisabled,
      inputStyles,
      noErrorState,
      dropDownStyles,
      xDeleteChar,
      hideXDelete,
      inputClassNames,
      outerWrapStyles,
      hideErrorMessage,
      isErrorTriggered,
      dropDownClassNames,
      outerWrapClassNames,
      requiredTextStyles,
      requiredTextClassNames,
      xDeleteClassNames,
      xDeleteStyles,
      arrowStyles,
      arrowClassNames,
      validationRules: { required },
      placeholder,
      errorMessage,
    } = this.props;

    const {
      value,
      isValid,
      uiValue,
      isTouched,
      isFocused,
      userQuery,
    } = this.state;

    const isErrorState = (!noErrorState
      && !isValid && !isFocused && isTouched)
      || (!isValid && isErrorTriggered && !isFocused);

    const options = this.getOptions();
    // what the user sees

    let outerWrapState = '';
    if (isDisabled) outerWrapState = '--disabled';
    else if (isValid && isFocused) outerWrapState = '--valid-dropdown';
    else if (isValid) outerWrapState = '--valid';
    else if (isFocused) outerWrapState = '--focused';
    else if (isErrorState) outerWrapState = '--error';

    let fullOuterWrapClassNames = `base-select__outer-wrap${outerWrapState} ${outerWrapClassNames}`;
    if (required) fullOuterWrapClassNames += ' base-select__outer-wrap--required';
    
    const isValuePresent = value && String(value).length > 0;

    return (
      <div
        style={outerWrapStyles}
        onClick={this.handleFocus}
        className={fullOuterWrapClassNames}
        ref={(node) => { this.node = node }}
      >
        {!value && placeholder && (
          <div className="base-select__placeholder">
            {placeholder}
          </div>
        )}
        {(!hideXDelete && isValuePresent) && (
          <span
            style={xDeleteStyles}
            onClick={this.handleXDelete}
            className={`base-select__x-delete ${xDeleteClassNames}`}
          >
            {xDeleteChar}
          </span>
        )}
        {(hideXDelete) && (
          <span
            style={arrowStyles}
            onClick={(e) => this.handleArrow(e)}
            className={`${isFocused ? `base-select__arrow--up` : `base-select__arrow--down` } ${arrowClassNames}`}
          />
        )}
        <input // <- this is the "fake input"
          type="text"
          value={userQuery}
          onKeyDown={this.handleKeyDown}
          onChange={this.handleUserQuery}
          className="base-select__hidden-input"
          ref={(hiddenInput) => { this.hiddenInput = hiddenInput }}
        />
        <div // <- this is the user facing "input"
          className={`base-select__input ${inputClassNames}`}
          style={inputStyles}
        >
          {uiValue}
        </div>
        {(!hideErrorMessage && isErrorState) && (
          <div
            style={requiredTextStyles}
            onClick={this.handleFocus}
            className={`base-select__required ${requiredTextClassNames}`}
          >
            {errorMessage}
          </div>
        )}
        {isFocused && (
          <ul
            ref={(dropDown) => { this.dropDown = dropDown }}
            style={dropDownStyles}
            className={`base-select__dropdown ${dropDownClassNames}`}
          >
            {options}
          </ul>
        )}
      </div>
    )
  }
}

// Ignore order of props below, they get alphabetized on build
BaseSelect.propTypes = {
  coachingFormType: PropTypes.any,
  requiredTextClassNames: PropTypes.string,
  hideXDelete: PropTypes.bool,
  xDeleteChar: PropTypes.string,
  xDeleteClassNames: PropTypes.string,
  xDeleteStyles: PropTypes.object,
  arrowClassNames: PropTypes.string,
  arrowStyles: PropTypes.object,
  outerWrapClassNames: PropTypes.string,
  value: PropTypes.any.isRequired,
  isDisabled: PropTypes.bool,
  inputClassNames: PropTypes.string,
  dropDownClassNames: PropTypes.string,
  optionClassNames: PropTypes.string,
  isErrorTriggered: PropTypes.bool,
  optionStyles: PropTypes.object,
  outerWrapStyles: PropTypes.object,
  dropDownStyles: PropTypes.object,
  inputStyles: PropTypes.object,
  inputKey: PropTypes.any.isRequired,
  eventHandler: PropTypes.func.isRequired,
  optionData: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.any.isRequired,
    text: PropTypes.string.isRequired,
    value: PropTypes.any.isRequired,
  })).isRequired,
  noErrorState: PropTypes.bool,
  hideErrorMessage: PropTypes.bool,
  errorMessage: PropTypes.string,
  requiredTextStyles: PropTypes.object,
  validationRules: PropTypes.object,
  placeholder: PropTypes.string,
};

// Assign all default props here, not within component
BaseSelect.defaultProps = {
  coachingFormType: '',
  outerWrapClassNames: '',
  errorMessage: 'This field is required.',
  outerWrapStyles: {},
  inputClassNames: '',
  inputStyles: {},
  optionClassNames: '',
  optionStyles: {},
  dropDownClassNames: '',
  dropDownStyles: {},
  hideErrorMessage: false,
  isErrorTriggered: false,
  isDisabled: false,
  noErrorState: false,
  hideXDelete: false,
  xDeleteClassNames: '',
  xDeleteStyles: {},
  xDeleteChar: '✖',
  arrowStyles: {},
  arrowClassNames: '',
  requiredTextClassNames: '',
  requiredTextStyles: {},
  validationRules: {},
  placeholder: null,
};

export default BaseSelect;



// WEBPACK FOOTER //
// ./src/features/common/BaseSelect.js